import {useCallback, useRef} from 'react';

import {
  type TileMedia,
  type TileMediaSpec,
  type TileMediaTracking,
} from '../../MediaCard';
import {isAvailableFilesApolloCacheHit} from '../utils/isCacheHit';
import {shouldNotLogBlobFetchSuccess} from './update';

/**
 * Pipeline timestamps for `TileMediaTracking`. Stamps once per successful
 * stage so later renders of the same result do not restamp.
 */
export function useFileDataTracking({
  skip,
  availableFilesData,
  tileMedia,
  tileMediaSpec,
  disableVideoStream,
  observedInitialNetworkLoad,
}: {
  skip: boolean;
  availableFilesData: unknown;
  tileMedia: TileMedia | null;
  tileMediaSpec: TileMediaSpec;
  disableVideoStream: boolean;
  observedInitialNetworkLoad: boolean;
}): {
  tracking: TileMediaTracking | null;
  resetTrackingTimestamps: () => void;
} {
  const availableFilesReceivedAtRef = useRef(0);
  const dataCachedAtRef = useRef(0);

  if (
    !skip &&
    availableFilesData != null &&
    // oxlint-disable-next-line react/refs
    availableFilesReceivedAtRef.current === 0
  ) {
    // oxlint-disable-next-line react/purity, react/refs
    availableFilesReceivedAtRef.current = performance.now();
  }
  // URLs arrive in the same AvailableFiles response, so urls duration is 0.
  // oxlint-disable-next-line react/refs
  const directUrlReceivedAt = availableFilesReceivedAtRef.current;
  // Images and HLS mark loaded without waiting on bytes: stamp blobFetch
  // and cacheUpdate at the URL instant so those stages are 0ms. Other
  // types wait until the real blob is on TileMedia via the panel blob store.
  // oxlint-disable-next-line react/refs
  if (tileMedia != null && dataCachedAtRef.current === 0) {
    if (
      shouldNotLogBlobFetchSuccess(tileMedia, tileMediaSpec, disableVideoStream)
    ) {
      // oxlint-disable-next-line react/refs
      dataCachedAtRef.current = directUrlReceivedAt;
    } else if (!tileMedia.isNullBlob) {
      // oxlint-disable-next-line react/purity, react/refs
      dataCachedAtRef.current = performance.now();
    }
  }

  const resetTrackingTimestamps = useCallback(() => {
    availableFilesReceivedAtRef.current = 0;
    dataCachedAtRef.current = 0;
  }, []);

  const isCacheHit = isAvailableFilesApolloCacheHit({
    hasAvailableFilesData: !skip && availableFilesData != null,
    observedInitialNetworkLoad,
  });

  return {
    // oxlint-disable-next-line react/refs
    tracking: trackingFromQuery({
      skip,
      // oxlint-disable-next-line react/refs
      availableFilesReceivedAt: availableFilesReceivedAtRef.current,
      directUrlReceivedAt,
      // oxlint-disable-next-line react/refs
      dataCachedAt: dataCachedAtRef.current,
      isCacheHit: skip || availableFilesData == null ? undefined : isCacheHit,
    }),
    resetTrackingTimestamps,
  };
}

export function trackingFromQuery({
  skip,
  availableFilesReceivedAt,
  directUrlReceivedAt,
  dataCachedAt,
  isCacheHit,
}: {
  skip: boolean;
  availableFilesReceivedAt: number;
  directUrlReceivedAt: number;
  dataCachedAt: number;
  isCacheHit?: boolean;
}): TileMediaTracking | null {
  if (skip) {
    return null;
  }
  return {
    availableFilesReceivedPerformanceMs: availableFilesReceivedAt,
    directUrlReceivedPerformanceMs: directUrlReceivedAt,
    blobFetchCompletePerformanceMs: dataCachedAt,
    dataCachedPerformanceMs: dataCachedAt,
    thumbnailsRequested: true,
    hlsSrcRequested: true,
    isCacheHit,
  };
}
